/**
 * ATT-3384 admin half — override-branding store (Zustand), keyed by override host.
 *
 * Holds the branding fetched for an active staging tenant-override target (see
 * `fetchBranding.ts`). The sync `config/tenants.ts` override resolution reads it
 * via `getOverrideBranding()`; `useTenantOverrideBranding()` populates it and
 * subscribes so the app re-renders (first-paint real staging → hydrate to the
 * target) when the fetch resolves.
 *
 * An entry's `branding` is `null` when the query resolved to an unknown/inactive
 * host — that is "resolved but not brandable", distinct from "not fetched yet"
 * (no entry at all). `getOverrideBranding` collapses both to null (→ stay real
 * staging); `hasOverrideBrandingEntry` distinguishes them so an unknown host is
 * not re-fetched on every mount.
 */

import { create } from "zustand";
import { normalizeHost } from "../../config/tenants";
import type { TenantBranding } from "./fetchBranding";

type OverrideBrandingEntry = { branding: TenantBranding | null };

type TenantOverrideBrandingState = {
  byHost: Record<string, OverrideBrandingEntry>;
  setOverrideBranding: (host: string, branding: TenantBranding | null) => void;
  reset: () => void;
};

export const useTenantOverrideBrandingStore = create<TenantOverrideBrandingState>((set) => ({
  byHost: {},
  setOverrideBranding: (host, branding) =>
    set((s) => ({ byHost: { ...s.byHost, [normalizeHost(host)]: { branding } } })),
  reset: () => set({ byHost: {} }),
}));

/** Sync read for `config/tenants.ts`: the host's branding, or null (unfetched or unknown). */
export function getOverrideBranding(host: string): TenantBranding | null {
  return useTenantOverrideBrandingStore.getState().byHost[normalizeHost(host)]?.branding ?? null;
}

/** True once a fetch for this host has resolved (success OR unknown-host null). */
export function hasOverrideBrandingEntry(host: string): boolean {
  return normalizeHost(host) in useTenantOverrideBrandingStore.getState().byHost;
}

export function setOverrideBranding(host: string, branding: TenantBranding | null): void {
  useTenantOverrideBrandingStore.getState().setOverrideBranding(host, branding);
}

/** Test-only: clear all fetched override branding. */
export function __resetForTests(): void {
  useTenantOverrideBrandingStore.getState().reset();
}
